/**
 * DeviceBlockedScreen — Shown when kiosk is accessed from an unregistered device.
 * Allows registration via a 6-character code generated by an admin.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

interface DeviceBlockedScreenProps {
  onRegistered: () => void;
}

export function DeviceBlockedScreen({ onRegistered }: DeviceBlockedScreenProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInput = useCallback((index: number, value: string) => {
    const char = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-1);
    setError(null);

    setCode(prev => {
      const next = [...prev];
      next[index] = char;
      return next;
    });

    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setCode(prev => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
    }
  }, [code]);

  const handleSubmit = useCallback(async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/workforce/kiosk-devices?action=register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: fullCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Registratie mislukt');
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem('kiosk_device_token', data.device_token);
      setSuccess(data.device_name);
      setTimeout(onRegistered, 1500);
    } catch {
      setError('Verbinding mislukt');
      setIsSubmitting(false);
    }
  }, [code, onRegistered]);

  // Auto-submit when all 6 characters are entered
  useEffect(() => {
    if (code.every(c => c) && !isSubmitting && !success) {
      handleSubmit();
    }
  }, [code, isSubmitting, success, handleSubmit]);

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-slate-800 mb-2">Apparaat geregistreerd</h2>
          <p className="text-slate-500 text-lg">{success}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-8 py-5">
        <div className="flex items-center gap-4 max-w-6xl mx-auto">
          <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center shadow">
            <span className="text-white font-bold text-lg">C</span>
          </div>
          <span className="text-slate-800 font-semibold text-xl">Cinco Cleaning</span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center">
          {/* Lock icon */}
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          <h1 className="text-2xl font-semibold text-slate-800 mb-2">
            Apparaat niet geregistreerd
          </h1>
          <p className="text-slate-500 mb-8">
            Voer de registratiecode in die je van de beheerder hebt gekregen.
          </p>

          {/* Code input */}
          <div className="flex justify-center gap-3 mb-6">
            {code.map((char, i) => (
              <input
                key={i}
                ref={el => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="text"
                maxLength={1}
                value={char}
                onChange={e => handleInput(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                disabled={isSubmitting}
                className={`w-14 h-16 text-center text-2xl font-mono font-bold rounded-xl border-2 transition-all outline-none
                  ${error ? 'border-red-300 bg-red-50' : char ? 'border-indigo-400 bg-white' : 'border-slate-200 bg-white'}
                  focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200
                  disabled:opacity-50`}
              />
            ))}
          </div>

          {error && (
            <p className="text-red-500 text-sm mb-4">{error}</p>
          )}

          {isSubmitting && (
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <span>Registreren...</span>
            </div>
          )}

          <p className="text-xs text-slate-400 mt-8">
            Geen code? Vraag een beheerder om een registratiecode te genereren via Instellingen.
          </p>
        </div>
      </main>
    </div>
  );
}
